// The final experiment report (brief §22).
//
// The computed section renders first and always; the AI narration sits below it and
// is clearly a summary of those numbers, not a replacement for them. The "not a
// diagnosis" line is stated by the app, not left to the model to remember.

import { useEffect, useState } from 'react';
import { api, type FinalReportResult } from '../lib/api.ts';
import { Button, Card, Delta, ErrorNote, SectionTitle, Spinner } from '../components/ui.tsx';
import { CONFOUNDER_LABELS, longDate, titleCase } from '../lib/format.ts';

const NEXT_STEP_LABELS: Record<string, string> = {
  continue: 'Continue',
  stop_no_meaningful_benefit: 'Stop — no meaningful benefit',
  extend_experiment: 'Extend the experiment',
  change_one_variable_and_repeat: 'Change one variable and repeat',
  discuss_with_clinician: 'Discuss with a clinician',
  insufficient_data: 'Not enough data to say',
};

export function Report({ id, navigate }: { id: string; navigate: (path: string) => void }) {
  const [result, setResult] = useState<FinalReportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.ai.report(id)
      .then(setResult)
      .catch((e) => setError((e as Error).message));
  }, [id]);

  if (error) return <ErrorNote message={error} />;
  if (!result) return <Spinner label="Putting the report together" />;

  const { analysis, report } = result;
  const { experiment, adherence } = analysis;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold">{experiment.name.replace(/^\[demo\]\s*/, '')}</h1>
        <p className="text-sm text-muted mt-1">
          {experiment.baseline_start && `${longDate(experiment.baseline_start)} – `}
          {experiment.intervention_end ? longDate(experiment.intervention_end) : 'ongoing'}
        </p>
      </header>

      {experiment.hypothesis && (
        <section>
          <SectionTitle>Hypothesis</SectionTitle>
          <Card><p className="text-sm">{experiment.hypothesis}</p></Card>
        </section>
      )}

      <section>
        <SectionTitle>What changed</SectionTitle>
        <Card>
          <ul className="space-y-3">
            {analysis.metrics.filter((m) => m.comparison.baseline_average !== null).map((m) => (
              <li key={m.metric.id}>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm font-medium">{m.metric.name}</span>
                  <Delta percent={m.comparison.percentage_change} direction={m.comparison.direction} />
                </div>
                <p className="text-xs text-muted mt-0.5 tabular-nums">
                  {m.comparison.baseline_average} at baseline → {m.comparison.current_average} during treatment
                  {' · '}{m.comparison.baseline_count + m.comparison.current_count} readings
                </p>
              </li>
            ))}
          </ul>
        </Card>
      </section>

      <section>
        <SectionTitle>Adherence</SectionTitle>
        <Card>
          <p className="text-sm">
            {adherence.percent === null
              ? 'Nothing was scheduled.'
              : `${adherence.completed} of ${adherence.expected} scheduled actions completed — ${adherence.percent}%.`}
          </p>
          {(adherence.missed > 0 || adherence.skipped > 0) && (
            <p className="text-xs text-muted mt-1">
              {adherence.missed} missed · {adherence.skipped} deliberately skipped
            </p>
          )}
        </Card>
      </section>

      {(analysis.side_effects.length > 0 || analysis.confounders.length > 0) && (
        <section>
          <SectionTitle>Side effects and confounders</SectionTitle>
          <Card className="space-y-2 text-sm">
            {analysis.side_effects.length > 0 && (
              <p><span className="text-muted">Side effects: </span>
                {[...new Set(analysis.side_effects.map((s) => s.name))].join(', ')}</p>
            )}
            {analysis.confounders.length > 0 && (
              <p><span className="text-muted">Other factors: </span>
                {[...new Set(analysis.confounders.map((c) => CONFOUNDER_LABELS[c.type] ?? titleCase(c.type)))].join(', ')}</p>
            )}
          </Card>
        </section>
      )}

      {experiment.conclusion && (
        <section>
          <SectionTitle>Your assessment</SectionTitle>
          <Card><p className="text-sm">{experiment.conclusion}</p></Card>
        </section>
      )}

      {report ? (
        <section>
          <SectionTitle>Summary</SectionTitle>
          <Card className="space-y-3">
            <p className="text-sm leading-relaxed">{report.summary}</p>

            {report.data_quality_notes.length > 0 && (
              <div className="pt-2 border-t border-hairline">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted mb-1">Limits of this experiment</p>
                <ul className="text-sm space-y-1">
                  {report.data_quality_notes.map((note, i) => <li key={i}>· {note}</li>)}
                </ul>
              </div>
            )}

            <div className="pt-2 border-t border-hairline">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted mb-1">Suggested next step</p>
              <p className="text-sm font-medium">{NEXT_STEP_LABELS[report.suggested_next_step] ?? report.suggested_next_step}</p>
              <p className="text-sm text-ink-2 mt-1">{report.next_step_reason}</p>
            </div>

            {report.questions_for_clinician.length > 0 && (
              <div className="pt-2 border-t border-hairline">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted mb-1">Questions for your doctor</p>
                <ul className="text-sm space-y-1">
                  {report.questions_for_clinician.map((q, i) => <li key={i}>· {q}</li>)}
                </ul>
              </div>
            )}

            <p className="text-xs text-muted pt-2 border-t border-hairline leading-relaxed">
              Confidence: {report.confidence}. This is a summary of your own recorded data.
              It is not a diagnosis and does not replace medical advice.
            </p>
          </Card>
        </section>
      ) : (
        <p className="text-sm text-muted">
          AI summaries are switched off, so this report is the figures alone.
        </p>
      )}

      <div className="flex gap-2">
        <Button variant="primary" onClick={() => navigate(`/experiments/${id}/doctor`)}>Doctor summary</Button>
        <a className="tap min-h-11 px-4 rounded-xl border border-line text-sm font-medium inline-flex items-center"
          href={api.settings.csvUrl(id)} download>Export CSV</a>
      </div>
    </div>
  );
}
